import React from 'react';

// Hand-drawn imperfect circle outline (SVG)
export const SketchCircle = ({ className = '', strokeWidth = 2, delay = 0 }) => (
  <svg
    viewBox="0 0 200 200"
    className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    fill="none"
  >
    <path
      d="M100,12 C144,12 188,52 188,100 C188,148 152,188 100,188 C48,188 12,148 12,100 C12,54 52,12 100,12 Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeDasharray="600"
      strokeDashoffset="600"
      style={{
        animation: `draw-path 1.4s ease-out ${delay}s forwards`,
      }}
    />
  </svg>
);

// Doodle arrow pointing to something
export const DoodleArrow = ({ className = '', delay = 0, direction = 'down' }) => {
  const rotations = {
    down: 0,
    up: 180,
    left: 90,
    right: -90,
    'down-right': -45,
    'down-left': 45,
  };
  return (
    <svg
      viewBox="0 0 80 80"
      className={`pointer-events-none ${className}`}
      fill="none"
      style={{ transform: `rotate(${rotations[direction] || 0}deg)` }}
    >
      <path
        d="M40,6 C38,20 42,36 38,52 C36,60 34,66 32,70"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="100"
        strokeDashoffset="100"
        style={{ animation: `draw-path 0.8s ease-out ${delay}s forwards` }}
      />
      <path
        d="M22,58 L32,72 L46,62"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="60"
        strokeDashoffset="60"
        style={{ animation: `draw-path 0.4s ease-out ${delay + 0.6}s forwards` }}
      />
    </svg>
  );
};

// Hand-drawn star
export const SketchStar = ({ className = '', delay = 0, style = {} }) => (
  <svg
    viewBox="0 0 40 40"
    className={`pointer-events-none ${className}`}
    style={style}
    fill="none"
  >
    <path
      d="M20,4 L24,16 L36,16 L26,24 L30,36 L20,28 L10,36 L14,24 L4,16 L16,16 Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="120"
      strokeDashoffset="120"
      style={{ animation: `draw-path 0.7s ease-out ${delay}s forwards` }}
    />
  </svg>
);

// Scribble underline / accent
export const SketchUnderline = ({ className = '', delay = 0 }) => (
  <svg
    viewBox="0 0 200 20"
    className={`pointer-events-none ${className}`}
    fill="none"
    preserveAspectRatio="none"
  >
    <path
      d="M4,12 C30,8 60,14 90,10 C120,6 150,14 180,10 C188,9 194,11 196,12"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeDasharray="220"
      strokeDashoffset="220"
      style={{ animation: `draw-path 0.9s ease-out ${delay}s forwards` }}
    />
  </svg>
);

// Squiggle / scribble decoration
export const SketchSquiggle = ({ className = '', delay = 0 }) => (
  <svg
    viewBox="0 0 100 30"
    className={`pointer-events-none ${className}`}
    fill="none"
  >
    <path
      d="M4,15 C12,4 20,26 28,15 C36,4 44,26 52,15 C60,4 68,26 76,15 C84,4 92,26 96,15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="150"
      strokeDashoffset="150"
      style={{ animation: `draw-path 1s ease-out ${delay}s forwards` }}
    />
  </svg>
);

// Tape strip decoration
export const TapeStrip = ({ className = '', rotation = 0 }) => (
  <div
    className={`absolute pointer-events-none ${className}`}
    style={{
      transform: `rotate(${rotation}deg)`,
      background:
        'repeating-linear-gradient(90deg, rgba(255,255,255,0.12) 0px, rgba(255,255,255,0.12) 6px, rgba(255,255,255,0.18) 6px, rgba(255,255,255,0.18) 12px)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      backdropFilter: 'blur(2px)',
    }}
  />
);

// Hand-drawn dashed circle (smaller decorative)
export const SketchDot = ({ className = '', delay = 0 }) => (
  <svg
    viewBox="0 0 30 30"
    className={`pointer-events-none ${className}`}
    fill="none"
  >
    <circle
      cx="15"
      cy="15"
      r="6"
      stroke="currentColor"
      strokeWidth="2"
      strokeDasharray="40"
      strokeDashoffset="40"
      style={{ animation: `draw-path 0.6s ease-out ${delay}s forwards` }}
    />
  </svg>
);

// Punk burst-star (jagged irregular star like Art Market poster)
export const BurstStar = ({ className = '', delay = 0, fill = 'currentColor' }) => (
  <svg
    viewBox="0 0 200 200"
    className={`pointer-events-none ${className}`}
    fill="none"
  >
    <path
      d="M100,6 L116,46 L156,18 L142,62 L188,58 L150,90 L194,108 L146,116 L172,156 L126,138 L120,184 L98,144 L72,184 L66,138 L24,156 L46,116 L4,108 L42,90 L10,58 L54,62 L40,18 L82,46 Z"
      fill={fill}
      stroke={fill}
      strokeWidth="2"
      strokeLinejoin="round"
      style={{
        animation: `pop-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}s forwards`,
        opacity: 0,
        transformOrigin: 'center',
      }}
    />
  </svg>
);

// Fisheye/bubble overlay for circles
export const FisheyeOverlay = () => (
  <>
    {/* Dark vignette around edges */}
    <div
      className="absolute inset-0 rounded-full pointer-events-none"
      style={{
        background:
          'radial-gradient(circle at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.85) 100%)',
        mixBlendMode: 'multiply',
      }}
    />
    {/* Top-left glossy highlight */}
    <div
      className="absolute inset-0 rounded-full pointer-events-none"
      style={{
        background:
          'radial-gradient(circle at 28% 22%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.15) 14%, transparent 32%)',
      }}
    />
    {/* Tiny twinkle highlight */}
    <div
      className="absolute pointer-events-none"
      style={{
        top: '18%',
        left: '24%',
        width: '6px',
        height: '6px',
        background: 'white',
        borderRadius: '50%',
        boxShadow: '0 0 8px rgba(255,255,255,0.9)',
      }}
    />
    {/* Inner dark ring for depth */}
    <div className="absolute inset-0 rounded-full pointer-events-none ring-1 ring-inset ring-black/40" />
  </>
);

export const SketchFrame = ({ className = '', delay = 0, strokeWidth = 2 }) => (
  <svg
    viewBox="0 0 200 200"
    preserveAspectRatio="none"
    className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    fill="none"
  >
    <path
      d="M6,8 C50,4 100,10 196,6 C194,50 198,100 194,196 C150,194 100,198 4,194 C8,150 4,100 6,8 Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      strokeDasharray="900"
      strokeDashoffset="900"
      style={{ animation: `draw-path 1.4s ease-out ${delay}s forwards` }}
    />
  </svg>
);
